import { translate } from "app/language/service"
import { getStore } from "app/store/store"
import { IDspDealAdvertiserDomain, IDspDealFormat, IDspDealSeat } from "dsp-deal/model"
import {
  getDspDealAdvertiserDomains,
  getDspDealFormats,
  getDspDealSeats,
  setDspDealAdvertiserDomains,
  setDspDealFormats,
  setDspDealSeats
} from "dsp-deal/tools/common"
import { IForm, IFormChange, IFormDataLoader } from "form/model"
import { formActionCreator } from "form/store/actions"
import { getFormatById } from "format/tools/common"
import { messageSVC } from "message/service"
import { getFinalizedArrayChange } from "./change"
import { getConfigurationChanges } from "./configuration"

export async function synchronizeDspDealAdvertiserDomains(form: IForm): Promise<void> {
  if (!form.context.isDspDeal) {
    return
  }
  const { tempDspDealId: dspDealId } = form
  const { dspDealAdvertiserDomains: existingAdvertiserDomains } = form.context.relations
  const { advertiserDomainIds: toSetAdvertiserDomainIds } = form.configuration.dspDeal
  if (dspDealId < 0) {
    return Promise.reject("dsp deal id must be equals or greater then 0")
  }
  if (!Array.isArray(toSetAdvertiserDomainIds)) {
    return Promise.reject("advertiserDomainIds must be an array")
  }
  for (const toSetAdvertiserDomainId of toSetAdvertiserDomainIds) {
    if (typeof toSetAdvertiserDomainId !== "number") {
      return Promise.reject(
        `Invalid value in advertiserDomainIds. Provided id="${toSetAdvertiserDomainId}" is not a number`
      )
    }
  }
  if (Array.isArray(existingAdvertiserDomains)) {
    for (const existingAdvertiserDomain of existingAdvertiserDomains) {
      if (typeof existingAdvertiserDomain !== "object") {
        return Promise.reject("An existing advertiser domain is invalid")
      }
    }
  }
  let hasError: boolean = false
  let serverSideAdvertiserDomains: IDspDealAdvertiserDomain[] = []
  try {
    serverSideAdvertiserDomains = await setDspDealAdvertiserDomains(dspDealId, toSetAdvertiserDomainIds)
  } catch (error) {
    hasError = true
    console.error("Unable to synchronize dsp deal advertiser domains", error)
  }
  if (hasError) {
    messageSVC.error(translate("dsp-deal.sync.advertiser-domain.error"))
    try {
      serverSideAdvertiserDomains = await getDspDealAdvertiserDomains(dspDealId)
    } catch (error) {
      // it doesn't smell good here ...
      console.error("Cannot fallback ...", error)
      return
    }
  }
  getStore().dispatch(
    formActionCreator.setRelations(form.context.appContext, {
      dspDealAdvertiserDomains: serverSideAdvertiserDomains
    })
  )
}

export async function synchronizeDspDealFormats(form: IForm, toSetFormatIds: number[]): Promise<void> {
  if (!form.context.isDspDeal) {
    return
  }
  const { tempDspDealId: dspDealId } = form
  const { dspDealFormats: existingFormats } = form.context.relations
  if (dspDealId < 0) {
    return Promise.reject("dsp deal id must be equals or greater then 0")
  }
  if (!Array.isArray(toSetFormatIds)) {
    return Promise.reject("formatIds must be an array")
  }
  for (const toSetFormatId of toSetFormatIds) {
    if (typeof toSetFormatId !== "number") {
      return Promise.reject(`Invalid value in formatIds. Provided id="${toSetFormatId}" is not a number`)
    }
  }
  if (Array.isArray(existingFormats)) {
    for (const existingFormat of existingFormats) {
      if (typeof existingFormat !== "object") {
        return Promise.reject("An existing format is invalid")
      }
    }
  }
  let hasError: boolean = false
  let serverSideFormats: IDspDealFormat[] = []
  getStore().dispatch(formActionCreator.startSynchronizingDspDealFormats(form.context.appContext))
  try {
    serverSideFormats = await setDspDealFormats(dspDealId, toSetFormatIds)
  } catch (error) {
    hasError = true
    console.error("Unable to synchronize dsp deal formats", error)
  }
  if (hasError) {
    messageSVC.error(translate("dsp-deal.sync.format.error"))
    try {
      serverSideFormats = await getDspDealFormats(dspDealId)
    } catch (error) {
      // it doesn't smell good here ...
      console.error("Cannot fallback ...", error)
      return
    }
  }
  getStore().dispatch(
    formActionCreator.setDspDealFormat(
      form.context.appContext,
      serverSideFormats,
      getConfigurationChanges({
        ...form,
        configuration: {
          ...form.configuration,
          dspDeal: {
            ...form.configuration.dspDeal,
            formatIds: serverSideFormats.map(f => f.id)
          }
        }
      })
    )
  )
}

export async function synchronizeDspDealSeats(form: IForm): Promise<void> {
  if (!form.context.isDspDeal) {
    return
  }
  const { tempDspDealId: dspDealId } = form
  const { dspDealSeats: existingSeats } = form.context.relations
  const { seatIds: toSetSeatIds } = form.configuration.dspDeal
  if (dspDealId < 0) {
    return Promise.reject("dsp deal id must be equals or greater then 0")
  }
  if (!Array.isArray(toSetSeatIds)) {
    return Promise.reject("seatIds must be an array")
  }
  for (const toSetSeatId of toSetSeatIds) {
    if (typeof toSetSeatId !== "number") {
      return Promise.reject(`Invalid value in seatIds. Provided id="${toSetSeatId}" is not a number`)
    }
  }
  if (Array.isArray(existingSeats)) {
    for (const existingSeat of existingSeats) {
      if (typeof existingSeat !== "object") {
        return Promise.reject("An existing seat is invalid")
      }
    }
  }
  let hasError: boolean = false
  let serverSideSeats: IDspDealSeat[] = []
  try {
    serverSideSeats = await setDspDealSeats(dspDealId, toSetSeatIds)
  } catch (error) {
    hasError = true
    console.error("Unable to synchronize dsp deal seats", error)
  }
  if (hasError) {
    messageSVC.error(translate("dsp-deal.sync.seat.error"))
    try {
      serverSideSeats = await getDspDealSeats(dspDealId)
    } catch (error) {
      // it doesn't smell good here ...
      console.error("Cannot fallback ...", error)
      return
    }
  }
  getStore().dispatch(
    formActionCreator.setRelations(form.context.appContext, {
      dspDealSeats: serverSideSeats
    })
  )
}

export function noticeAndClearDspDealFormatError(form: IForm): void {
  messageSVC.error(translate("dsp-deal.formats.error.selection"))
  getStore().dispatch(formActionCreator.setRelations(form.context.appContext, { dspDealFormatsError: false }))
}

export const dspDealFormDataLoader: IFormDataLoader = async (form: IForm, onFinish: (error?: string) => any) => {
  try {
    if (!form.context.isDspDeal) {
      onFinish()
      return
    }
    const advertiserDomains = await getDspDealAdvertiserDomains(form.tempDspDealId)
    form.context.relations.dspDealAdvertiserDomains = advertiserDomains
    const formats = await getDspDealFormats(form.tempDspDealId)
    form.context.relations.dspDealFormats = formats
    const seats = await getDspDealSeats(form.tempDspDealId)
    form.context.relations.dspDealSeats = seats
    form.configuration.dspDeal.advertiserDomainIds = advertiserDomains.map(a => a.advertiser_domain_id)
    form.configuration.dspDeal.formatIds = formats.map(f => f.format_id)
    form.configuration.dspDeal.seatIds = seats.map(s => s.seat_id)
    onFinish()
  } catch (error) {
    console.error(error)
    onFinish(`Unexpected error while loading dsp-deal data: ${error}`)
  }
}

export function getDspDealFormatChanges(form: IForm): IFormChange[] {
  const previousData = form.context.initialData
  const previousFormatIds = !previousData ? [] : previousData.configuration.dspDeal.formatIds
  const changes: IFormChange[] = []
  changes.push(
    getFinalizedArrayChange(
      "label.format",
      previousFormatIds,
      form.configuration.dspDeal.formatIds,
      (id: any) => getFormatById(id)?.name || id
    )
  )
  return changes
}
